/**
 * Virtual Fitting Room + Size Chart links for the Easy Bundle Builder
 * app's ("window.gbb") build-your-own bundle flow.
 *
 * BEST-EFFORT / NOT LIVE-VERIFIED. This was written without a browser
 * available to observe the app's actual client-rendered markup — the
 * detection heuristics below (isSelectedProductLink, findContainer) are
 * educated guesses based on common bundle-app conventions, not confirmed
 * against this app's real DOM. If links don't appear when picking a
 * component in a real browser, start by inspecting the selected product
 * card's actual classes/attributes and adjusting isSelectedProductLink.
 *
 * Approach once a selection IS detected correctly:
 *   1. Extract the product handle from the selected card's product link.
 *   2. Fetch /products/<handle>.js (public Shopify endpoint) for real
 *      option/variant data.
 *   3. If it has a real Size-type option (same broadened match as
 *      product-variant-picker-block.liquid: exact "size" or containing
 *      "(size)", to also catch this catalog's verbose compression-sock
 *      option names), build the trigger buttons + a modal.
 *   4. The modal is a clone of the inert <template> in
 *      apparel-bundle-size-links.liquid (itself just size-finder-modal.liquid
 *      rendered with render_as_template: true) — guarantees it always
 *      matches whatever initModal() in size-finder.js actually expects,
 *      rather than a hand-duplicated copy here that could drift out of
 *      sync. Appending it to <body> is enough to get it initialized: the
 *      MutationObserver in size-finder.js (extended for this) picks up
 *      any new [data-size-finder] root and calls initModal() on it.
 */
(function () {
  if (!window.gbb) return;
  if (window.__apparelBundleSizeLinksInit) return;
  window.__apparelBundleSizeLinksInit = true;

  var productCache = {};
  var instanceCounter = 0;

  function isSizeOptionName(name) {
    var n = (name || '').toLowerCase();
    return n === 'size' || n.indexOf('(size)') !== -1;
  }

  // ASSUMPTION (unverified): a bundle-builder product card marks itself
  // "selected" via a class containing one of these words, or via
  // aria-selected="true", or via a checked radio/checkbox in the same
  // card. Broad on purpose — cheaper to false-positive on an unrelated
  // "active" class than to miss the real one entirely.
  var SELECTED_HINT = /(^|[-_\s])(selected|active|chosen|is-selected|is-active|is-chosen)([-_\s]|$)/i;

  function isWithinBundleWidget(el) {
    return !!el.closest('[class*="gbb" i], [id*="gbb" i]');
  }

  function isSelectedProductLink(link) {
    if (!isWithinBundleWidget(link)) return false;
    var card = link.closest('[class*="gbb" i]') || link.parentElement;
    if (!card) return false;
    if (SELECTED_HINT.test(link.className) || SELECTED_HINT.test(card.className)) return true;
    if (link.getAttribute('aria-selected') === 'true' || card.getAttribute('aria-selected') === 'true') return true;
    var checkedInput = card.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
    if (checkedInput) return true;
    return false;
  }

  function extractHandle(href) {
    var match = /\/products\/([a-zA-Z0-9-]+)/.exec(href || '');
    return match ? match[1] : null;
  }

  // Best-effort "which real DOM node represents this bundle slot" so a
  // changed selection replaces its own links rather than piling up new
  // ones. Falls back to the link's immediate parent if no gbb-classed
  // ancestor is found within a few levels.
  function findSlotContainer(link) {
    var el = link;
    for (var i = 0; i < 6 && el; i++) {
      if (el !== link && /gbb/i.test(el.className || '')) return el;
      el = el.parentElement;
    }
    return link.parentElement;
  }

  function buildTriggerMarkup(instanceId) {
    var wrap = document.createElement('div');
    wrap.className = 'size-finder__triggers';
    wrap.setAttribute('data-size-finder-triggers', '');
    wrap.setAttribute('data-instance-id', instanceId);
    wrap.setAttribute('data-apparel-bundle-size-trigger', '');
    wrap.innerHTML =
      '<button type="button" class="size-finder__trigger-link" data-size-finder-trigger>' +
        '<span>Find Your Size</span>' +
        '<span class="size-finder__trigger-arrow" aria-hidden="true">&rarr;</span>' +
      '</button>' +
      '<button type="button" class="size-finder__trigger-link" data-size-finder-chart-trigger>' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
          '<rect x="2.5" y="8" width="19" height="8" rx="1" stroke-linejoin="round"/>' +
          '<path d="M6 8v3M9.5 8v2M13 8v3M16.5 8v2" stroke-linecap="round"/>' +
        '</svg>' +
        '<span>Size Chart</span>' +
      '</button>';
    return wrap;
  }

  function computeInStockSizes(product, sizeOption) {
    var inStock = [];
    sizeOption.values.forEach(function (value) {
      var found = product.variants.some(function (variant) {
        if (!variant.available) return false;
        var key = 'option' + sizeOption.position;
        return variant[key] === value;
      });
      if (found) inStock.push(value);
    });
    return inStock;
  }

  function buildModal(instanceId, product, sizeOption) {
    var tpl = document.querySelector('[data-apparel-bundle-modal-template]');
    if (!tpl) return null;
    var frag = tpl.content.cloneNode(true);
    var root = frag.querySelector('[data-size-finder]');
    if (!root) return null;

    root.setAttribute('data-instance-id', instanceId);
    root.setAttribute('data-apparel-bundle-size-modal', '');

    var configEl = root.querySelector('[data-size-finder-config]');
    if (configEl) {
      configEl.textContent = JSON.stringify({
        availableSizes: sizeOption.values,
        inStockSizes: computeInStockSizes(product, sizeOption),
        chartOverride: (product.metafields && product.metafields.sizefinder) || null,
        productHandle: product.handle,
        sectionId: instanceId,
        optionName: sizeOption.name,
        optionPosition: sizeOption.position
      });
    }

    root.querySelectorAll('[data-size-finder-image-link]').forEach(function (a) {
      a.href = product.url || ('/products/' + product.handle);
    });
    if (product.featured_image) {
      root.querySelectorAll('[data-size-finder-image-link] img').forEach(function (img) {
        img.src = product.featured_image;
        img.alt = product.title || '';
      });
    }

    document.body.appendChild(frag);
    return document.querySelector('[data-size-finder][data-instance-id="' + window.CSS.escape(instanceId) + '"]');
  }

  function applyLinksForSelection(container, handle) {
    if (container.__apparelBundleSizeLinks && container.__apparelBundleSizeLinks.handle === handle) {
      return; // same selection already applied, nothing to do
    }

    // Selection changed: tear down whatever this slot showed before.
    if (container.__apparelBundleSizeLinks) {
      var prev = container.__apparelBundleSizeLinks;
      if (prev.triggerEl && prev.triggerEl.parentNode) prev.triggerEl.parentNode.removeChild(prev.triggerEl);
      if (prev.modalEl && prev.modalEl.parentNode) prev.modalEl.parentNode.removeChild(prev.modalEl);
    }
    container.__apparelBundleSizeLinks = { handle: handle, triggerEl: null, modalEl: null };

    var fetchPromise = productCache[handle];
    if (!fetchPromise) {
      fetchPromise = fetch('/products/' + handle + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
      productCache[handle] = fetchPromise;
    }

    fetchPromise.then(function (product) {
      if (!product) return;
      // Selection may have moved on again while this fetch was in flight.
      if (!container.__apparelBundleSizeLinks || container.__apparelBundleSizeLinks.handle !== handle) return;

      var sizeOption = (product.options || []).find(function (opt) {
        return isSizeOptionName(opt.name) && opt.values.length > 1;
      });
      if (!sizeOption) return; // e.g. a bag/accessory slot — nothing to show

      instanceCounter += 1;
      var instanceId = 'apparel-bundle-' + handle + '-' + instanceCounter;

      var triggerEl = buildTriggerMarkup(instanceId);
      container.insertAdjacentElement('afterend', triggerEl);

      var modalEl = buildModal(instanceId, product, sizeOption);

      container.__apparelBundleSizeLinks.triggerEl = triggerEl;
      container.__apparelBundleSizeLinks.modalEl = modalEl;
    });
  }

  function scan() {
    document.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
      if (!isSelectedProductLink(link)) return;
      var handle = extractHandle(link.getAttribute('href'));
      if (!handle) return;
      var container = findSlotContainer(link);
      applyLinksForSelection(container, handle);
    });
  }

  var pending = false;
  function scheduleScan() {
    if (pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      scan();
    }, 200);
  }

  var observer = new MutationObserver(scheduleScan);

  function start() {
    scan();
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
